import React from "react";

/**
 * Primitives UI de la refonte FormsNewOrEdit (handoff "FormNewOrEdit v2").
 * Ajouts validés PO (cf. README handoff §Ajouts à valider) : switch-cards,
 * steppers − n +, cartes acte, segmented pill. Tokens : DESIGN.md (aucun nouveau).
 */

export const UI = {
    navy: "#213067",
    green: "#88bc24",
    greenHover: "#7bb421",
    greenLight: "#f0ffd8",
    red: "#B22222",
    hairline: "#E3E7EC",
    hairlineSoft: "#EEF1F4",
    fieldBorder: "#C9D4E8",
    text: "#2D333B",
    textSoft: "#5A6470",
    textMuted: "#8A929C",
    numBg: "#EAEEF6",
};

export const cardBaseSx = (hovered) => ({
    background: "#fff",
    border: `1px solid ${hovered ? UI.green : UI.hairline}`,
    borderRadius: 14,
    boxShadow: hovered
        ? `0 0 0 3px ${UI.greenLight}, 0 1px 3px rgba(33,48,103,.05)`
        : "0 1px 2px rgba(33,48,103,.06), 0 1px 3px rgba(33,48,103,.05)",
    transition: "box-shadow .25s, border-color .25s",
    overflow: "hidden",
});

export function Eyebrow({children, style}) {
    return (
        <div style={{fontSize: 12, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: UI.navy, ...style}}>
            {children}
        </div>
    );
}

export function FieldLabel({children, required = false}) {
    return (
        <div style={{fontSize: 13, fontWeight: 600, color: UI.textSoft, marginBottom: 6}}>
            {children} {required && <span style={{color: UI.red}}>*</span>}
        </div>
    );
}

export function Hairline({my = 18}) {
    return <div style={{height: 1, background: UI.hairlineSoft, margin: `${my}px 0`}}/>;
}

/** Carte-switch cliquable entière (galerie, relevé terrain, lavage partiel). */
export function SwitchCard({checked, onToggle, title, subtitle}) {
    return (
        <div
            onClick={() => onToggle(!checked)}
            style={{
                flex: 1, display: "flex", alignItems: "center", gap: 12,
                border: `1px solid ${UI.hairline}`, borderRadius: 10, padding: "12px 14px",
                cursor: "pointer", background: checked ? UI.greenLight : "#fff",
                transition: "background .25s",
            }}
        >
            <span style={{
                width: 40, height: 24, borderRadius: 999, position: "relative", flexShrink: 0,
                transition: "background .25s", background: checked ? UI.green : "#cfd3dc",
            }}>
                <span style={{
                    position: "absolute", top: 3, width: 18, height: 18, borderRadius: "50%",
                    background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.2)",
                    transition: "left .25s", left: checked ? 19 : 3,
                }}/>
            </span>
            <span>
                <span style={{display: "block", fontSize: 14, fontWeight: 600, color: UI.text}}>{title}</span>
                <span style={{display: "block", fontSize: 12, color: UI.textMuted}}>{subtitle}</span>
            </span>
        </div>
    );
}

/** Stepper − n + (photos), borné min/max. */
export function Stepper({value, onChange, min = 0, max = 5}) {
    const v = Number(value) || 0;
    const btn = {
        width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center",
        justifyContent: "center", cursor: "pointer", fontWeight: 700, userSelect: "none", fontSize: 15,
    };
    return (
        <span style={{display: "inline-flex", alignItems: "center", gap: 10}}>
            <span
                onClick={() => onChange(Math.max(min, v - 1))}
                style={{...btn, border: `1.5px solid ${UI.fieldBorder}`, color: UI.navy, background: "#fff", opacity: v <= min ? .45 : 1}}
            >−</span>
            <span style={{minWidth: 18, textAlign: "center", fontSize: 15, fontWeight: 700, color: UI.navy}}>{v}</span>
            <span
                onClick={() => onChange(Math.min(max, v + 1))}
                style={{...btn, background: UI.navy, color: "#fff", opacity: v >= max ? .45 : 1}}
            >+</span>
        </span>
    );
}

/** Mini-carte photo (label + stepper + hint). */
export function PhotoStepperCard({label, value, onChange, hintZero, hintSome}) {
    const v = Number(value) || 0;
    return (
        <div style={{flex: 1, border: `1px solid ${UI.hairline}`, borderRadius: 10, padding: "12px 14px"}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <span style={{fontSize: 13.5, fontWeight: 600, color: UI.text}}>{label}</span>
                <Stepper value={v} onChange={onChange}/>
            </div>
            <div style={{fontSize: 11.5, color: UI.textMuted, marginTop: 4}}>
                {v === 0 ? hintZero : hintSome.replace("{n}", String(v))}
            </div>
        </div>
    );
}

/** Segmented pill Facultative / Obligatoire. */
export function SaisieSegment({obligatoire, onChange, saving = false}) {
    const seg = (active, activeBg) => ({
        padding: "5px 14px", borderRadius: 999, fontSize: 12.5, fontWeight: 600,
        cursor: "pointer", transition: "all .2s", userSelect: "none",
        background: active ? activeBg : "transparent",
        color: active ? "#fff" : UI.textSoft,
        boxShadow: active ? "0 1px 3px rgba(33,48,103,.18)" : "none",
        opacity: saving ? .6 : 1,
    });
    return (
        <div style={{display: "inline-flex", background: UI.hairlineSoft, borderRadius: 999, padding: 3}}>
            <span onClick={() => !saving && onChange(false)} style={seg(!obligatoire, UI.textSoft)}>Facultative</span>
            <span onClick={() => !saving && onChange(true)} style={seg(obligatoire, UI.greenHover)}>Obligatoire</span>
        </div>
    );
}

/** Pastille numéro d'acte. */
export function ActeNumber({n, principal = false}) {
    return (
        <span style={{
            width: 24, height: 24, borderRadius: "50%", fontSize: 13, fontWeight: 800, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: principal ? UI.green : UI.numBg, color: UI.navy,
        }}>{n}</span>
    );
}

/** Bouton d'action icône du header d'acte (monter/descendre/retirer). */
export function HeaderIconButton({title, onClick, disabled = false, danger = false, children}) {
    return (
        <span
            title={title}
            onClick={disabled ? undefined : onClick}
            style={{
                width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center",
                justifyContent: "center", cursor: disabled ? "default" : "pointer",
                color: disabled ? UI.fieldBorder : (danger ? UI.red : UI.textSoft),
            }}
        >{children}</span>
    );
}
